// Side-effect: load Vue runtime before any .vue file. See vue-runtime.js
// for the full reasoning. This file lives at the top of the import order
// because the relative path sorts before any aliased imports.
import './vue-runtime';
import '@mixins/global-mixin.js';
import '@mixins/lodash-global-mixin.js';
import 'floating-vue/dist/style.css';
import 'vue-toastification/dist/index.css';
import 'vue3-perfect-scrollbar/style.css';

import XlinxFeatureComponent from '@components/xlinx-feature.vue';
import * as Sentry from '@sentry/vue';
import { createHead } from '@vueuse/head';
import FloatingVue, {
    Dropdown as FvDropdown,
    Menu as FvMenu,
    PopperContent as FvPopperContent,
    PopperWrapper as FvPopperWrapper,
    Tooltip as FvTooltip,
    TooltipDirective as FvTooltipDirective,
    vTooltip,
} from 'floating-vue';
import Cookies from 'js-cookie';
import VueViewer from 'v-viewer';
import vhCheck from 'vh-check';
import Vue, { configureCompat, createApp, h } from 'vue';
import VueSignaturePad from 'vue-signature-pad';
import VueSweetalert2 from 'vue-sweetalert2';
import Toast from 'vue-toastification';
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar';
import { Workbox } from 'workbox-window';

import { runStartupCleanup } from '@/local-storage-cleanup';
import XlinxFeature from '@/plugins/xlinx-feature';
import XlinxPermissions from '@/plugins/xlinx-permissions';
import RouteCheckVersion from '@/route.check-version';
import { getRoutesFromUserType } from '@/routes';

import App from './app.vue';
import router from './router';
import store from './store';

runStartupCleanup();

configureCompat({
    MODE: 2,
    // Codebase has zero manual `render(h, ctx)` functions — every component
    // uses templates. Opt out of Vue 2-style render compat globally so the
    // warning doesn't fire for the auto-generated render of every component.
    RENDER_FUNCTION: false,
    // Vue 2 fired watchers automatically when array props were mutated
    // (push/splice) without `deep: true`. Vue 3 doesn't, and the compat
    // layer logs WATCH_ARRAY for every Array-typed watch site even where
    // the consumer doesn't rely on the old behavior. Watchers that need
    // deep tracking already declare `deep: true` (or watch a derived
    // identity); turning compat off matches Vue 3 native behavior cleanly.
    WATCH_ARRAY: false,
});

// Vue 2 plugins (replaced in Sections 2-3 by Vue 3 equivalents).
// In @vue/compat MODE 2, the legacy Vue.use pattern still applies plugin
// install() functions via the compat layer; switching to app.use here
// would call install(app) which most Vue-2-only plugins don't expect.
Vue.use(VueViewer);

const app = createApp({
    render: () => h(App),
});

app.config.compilerOptions.isCustomElement = (tag) => tag === 'docuseal-form';

app.component('XlinxFeature', XlinxFeatureComponent);

const head = createHead();
app.use(head);
app.use(XlinxPermissions);
app.use(XlinxFeature);
app.use(VueSignaturePad);
// vue-sweetalert2's install detects the modern app instance and writes
// $swal to app.config.globalProperties. Calling Vue.use() (legacy) before
// createApp would set it on the legacy Vue's globals which the new app
// does not inherit, so consumers see `this.$swal is not a function`.
app.use(VueSweetalert2, {
    buttonsStyling: false,
    reverseButtons: true,
    customClass: {
        cancelButton: 'bg-gray-700 p-4 rounded text-white mr-1 outline-hidden',
        confirmButton: 'bg-blue-700 p-4 rounded text-white outline-hidden',
    },
});
// Mirror $swal onto the legacy Vue static so existing non-component callers
// (mixins/form-model.js, models/proposal-wizard.js, models/order.js) keep
// working. vue-sweetalert2's Vue 3 install path only writes to
// app.config.globalProperties; the Vue 2 path used to also set Vue.swal.
Vue.swal = app.config.globalProperties.$swal;
// floating-vue@5 components are pure Vue 3. Under @vue/compat MODE 2, the
// compat layer wraps their renders/slots and the destructured slot args
// inside VTooltipDirective.render crash with "(destructured parameter) is
// undefined". Opt every floating-vue component (including the nested
// `components` registries which reference internal Popper instances) out
// of compat per-component (MODE 3 = pure Vue 3 semantics) before installing
// the plugin.
const optOutOfCompat = (component) => {
    if (!component || typeof component !== 'object') return;
    if (Object.prototype.hasOwnProperty.call(component, 'compatConfig')) return;
    // eslint-disable-next-line no-param-reassign
    component.compatConfig = { MODE: 3 };
    if (component.components) {
        Object.values(component.components).forEach(optOutOfCompat);
    }
};
[FvDropdown, FvMenu, FvPopperContent, FvPopperWrapper, FvTooltip, FvTooltipDirective].forEach(
    optOutOfCompat,
);
app.use(FloatingVue);

// floating-vue@5's v-tooltip directive destructures `binding.value` and
// throws "[destructured parameter] is undefined" when consumers pass an
// undefined value (which v-tooltip@2 tolerated silently). Re-register the
// global v-tooltip directive after FloatingVue installs it, with a thin
// wrapper that no-ops on null/undefined values before delegating to the
// real directive. floating-vue's directive only exposes beforeMount,
// updated, and beforeUnmount — match that shape exactly.
const safeTooltip = {
    beforeMount(el, binding, vnode, prevVnode) {
        if (binding.value == null) return undefined;
        return vTooltip.beforeMount.call(this, el, binding, vnode, prevVnode);
    },
    updated(el, binding, vnode, prevVnode) {
        if (binding.value == null) return undefined;
        return vTooltip.updated.call(this, el, binding, vnode, prevVnode);
    },
    beforeUnmount(el, binding, vnode, prevVnode) {
        return vTooltip.beforeUnmount.call(this, el, binding, vnode, prevVnode);
    },
};
app.directive('tooltip', safeTooltip);
app.use(Toast);
app.use(PerfectScrollbarPlugin);

const feedback = Sentry.feedbackIntegration({
    autoInject: false,
    colorScheme: 'system',
    useSentryUser: { name: 'name', email: 'email' },
    triggerLabel: '',
    formTitle: 'Feedback',
    showBranding: false,
    submitButtonLabel: 'Send',
    messagePlaceholder: 'Describe the issue or feature request, please include order/invoice/account numbers, what are you trying to accomplish, and any other relevant details.',
    successMessageText: 'Thank you for your feedback!',
});

Sentry.init({
    app,
    dsn: process.env.MIX_SENTRY_LARAVEL_DSN,
    integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({ maskAllInputs: false, maskAllText: false }),
        feedback,
    ],
    tracesSampleRate: 0.25,
    replaysOnErrorSampleRate: 1,
    replaysSessionSampleRate: 1,
    debug: false,
    environment: process.env.NODE_ENV,
    enabled: process.env.MIX_SENTRY_ENABLED,
    maxValueLength: 1000,
});

if (window.location.pathname !== '/ai-chat-popout') {
    feedback.createWidget();
}

app.use(router);
app.use(store);

vhCheck({ force: true, cssVarName: 'browser-address-bar' });

let routesAdded = false;

router.onError((error) => {
    if (/ChunkLoadError:.*failed./i.test(error.message)) {
        window.location.reload();
    } else if (/Loading.*chunk.*failed./i.test(error.message)) {
        window.location.reload();
    } else if (error.name !== 'NavigationDuplicated') {
        throw error;
    }
});

try {
    Sentry.setTag('version', new Date(COMMIT_HASH).toISOString());
    // eslint-disable-next-line no-empty
} catch (e) {}
Sentry.setTag('version_raw', COMMIT_HASH);

// Vue Router 4 requires beforeEach to call `next()` exactly once per
// navigation. The Vue 2 / Router 3 version of this guard delegated next()
// to RouteCheckRoles AND let addRoute happen after — producing both the
// "next callback called more than once" warning AND a blank page (router 4's
// addRoute does NOT retroactively match the in-flight navigation; the
// canonical fix is to re-trigger with next({ ...to, replace: true }) AFTER
// addRoute and BEFORE doing role/feature gating).
//
// This guard inlines the role check rather than delegating to the
// router.check-roles.js helper because that helper was written for the
// Router 3 multi-next() contract.
router.beforeEach(async (to, from, next) => {
    try {
        await RouteCheckVersion(to, from, next, store);
    } catch (e) {
        // version check is best-effort; never fail navigation on it
    }

    if (to.meta.public) {
        next();
        return;
    }

    try {
        // Seed the auth store so getRoutesFromUserType has user.type.
        await store.dispatch('AuthenticationStore/getUser');

        // Register dynamic, user-type-specific routes BEFORE matching `to`.
        // addRoute() in vue-router@4 does not match the in-flight navigation,
        // so after a first-time registration we re-trigger with
        // next({ ...to, replace: true }) and short-circuit.
        if (!routesAdded) {
            getRoutesFromUserType(
                store.state.AuthenticationStore.user.type,
                app.config.globalProperties.$feature,
            ).forEach((route) => router.addRoute(route));
            routesAdded = true;
            store.dispatch('UiStore/checkFeature', 'invoicing');
            // Use the string path form: spreading `to` carries an empty
            // `matched: []` from the failed pre-addRoute resolution and
            // confuses vue-router's resolver on the re-trigger.
            next({ path: to.fullPath, replace: true });
            return;
        }

        // Routes are registered: gate by role.
        if (to.meta.roles) {
            const userRoles = store.getters['AuthenticationStore/roles'] || [];
            const hasRole = to.meta.roles.some((role) => userRoles.includes(role));
            if (!hasRole) {
                next({ path: '/403' });
                return;
            }
        }

        // Feature gate (inlined from route.check-feature.js for the same
        // single-next() reason as roles above).
        if (to.meta && to.meta.featureName) {
            const { $feature } = app.config.globalProperties;
            if ($feature && !$feature.enabled(to.meta.featureName)) {
                next({ path: '/404' });
                return;
            }
        }

        store.dispatch('UiStore/checkFeature', 'invoicing');
        next();
    } catch (e) {
        if (e.statusCode === 503) {
            next({ path: '/503' });
            return;
        }
        Cookies.remove('XSRF-TOKEN');
        Cookies.remove('xlinx_session');
        next({
            path: '/login',
            query: { redirect: to.fullPath },
        });
    }
});

const enabledServiceWorker = process.env.NODE_ENV === 'production';
if ('serviceWorker' in navigator && enabledServiceWorker) {
    const wb = new Workbox('/service-worker.js');
    wb.register();
}

app.mount('#app');

export default app;
